import React, { useEffect, useState } from "react";
import { api, downloadExport } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { LIBRARY } from "@/constants/testIds";
import { toast } from "sonner";
import { FileText, Download, Trash2, X, Loader2 } from "lucide-react";

export default function DocumentLibrary() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);   // full doc
  const [openLoading, setOpenLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } catch {
      toast.error("Could not load library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const view = async (id) => {
    setOpenLoading(true);
    try {
      const { data } = await api.get(`/documents/${id}`);
      setOpen(data);
    } catch {
      toast.error("Could not open document");
    } finally {
      setOpenLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/documents/${id}`);
      setDocs(docs.filter((d) => d.id !== id));
      if (open?.id === id) setOpen(null);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="overline mb-2 text-slate-500">SAVED DRAFTS</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          Document library.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-500">
          Every draft you generate is saved automatically. Re-open, export, or discard.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : docs.length === 0 ? (
        <div className="border border-slate-200 bg-white p-10 text-center">
          <FileText className="mx-auto mb-4 h-8 w-8 text-slate-300" strokeWidth={1.5} />
          <div className="overline text-slate-500">NO DOCUMENTS YET</div>
          <p className="mt-2 text-sm text-slate-500">
            Head to the Drafting tab and generate your first submission.
          </p>
        </div>
      ) : (
        <div className="grid gap-px bg-slate-100 sm:grid-cols-2 xl:grid-cols-3">
          {docs.map((d) => (
            <div
              key={d.id}
              data-testid={LIBRARY.item}
              className="group flex flex-col justify-between bg-[#FAFAF7] p-5 transition-all hover:bg-slate-50"
            >
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-[10px] text-[#2563EB]">/{d.doc_type}</span>
                  <span className="overline text-slate-400">{d.created_at?.slice(0, 10)}</span>
                </div>
                <div className="font-serif text-lg font-semibold leading-tight text-slate-900">
                  {d.title}
                </div>
              </div>
              <div className="mt-6 flex items-center gap-2">
                <button
                  data-testid={LIBRARY.openBtn}
                  onClick={() => view(d.id)}
                  className="flex-1 border border-slate-200 py-2 text-xs font-medium tracking-wide text-slate-700 transition-all hover:border-[#2563EB] hover:text-slate-900"
                >
                  Open
                </button>
                <button
                  data-testid={LIBRARY.deleteBtn}
                  onClick={() => remove(d.id)}
                  className="border border-slate-200 p-2 text-slate-500 transition-all hover:border-red-500/60 hover:text-red-400"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View modal */}
      {(open || openLoading) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4" onClick={() => setOpen(null)}>
          <div
            className="relative flex max-h-[92vh] w-full max-w-4xl flex-col border border-slate-200 bg-[#FAFAF7]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div className="min-w-0 flex-1">
                <div className="overline text-[#2563EB]">/{open?.doc_type}</div>
                <div className="truncate font-serif text-lg font-semibold">{open?.title || "Loading…"}</div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {open && (
                  <>
                    <button
                      onClick={() => downloadExport(open.title, open.content, "pdf")}
                      className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-[#2563EB] hover:text-slate-900"
                    >
                      <Download className="h-3.5 w-3.5" /> PDF
                    </button>
                    <button
                      onClick={() => downloadExport(open.title, open.content, "docx")}
                      className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-[#2563EB] hover:text-slate-900"
                    >
                      <Download className="h-3.5 w-3.5" /> DOCX
                    </button>
                  </>
                )}
                <button
                  onClick={() => setOpen(null)}
                  className="border border-slate-200 p-1.5 text-slate-500 hover:border-slate-300 hover:text-slate-900"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 lg:p-10">
              {open ? <Markdown>{open.content}</Markdown> : (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
